import { useState, useEffect, useCallback } from 'react'
import type {
  MapStyle,
  MapStyleSettingsOutput,
  UpdateMapStyleSettingsInput,
} from '../../types/infrastructure-map'
import { getStyleSettings, updateStyleSettings } from '../../services/infrastructure-map'
import { logger } from '../../utils/logger'

export interface UseMapStyleSettingsResult {
  settings: MapStyleSettingsOutput | null
  loading: boolean
  error: string | null
  updateSettings: (input: UpdateMapStyleSettingsInput) => Promise<MapStyleSettingsOutput | null>
  setRootNode: (nodeId: string | null) => Promise<MapStyleSettingsOutput | null>
  refetch: () => Promise<void>
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function useMapStyleSettings(
  caseId: string | null,
  mapStyle: MapStyle
): UseMapStyleSettingsResult {
  const [settings, setSettings] = useState<MapStyleSettingsOutput | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    if (!caseId) {
      setSettings(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getStyleSettings(caseId, mapStyle)
      setSettings(data)
    } catch (err) {
      logger.warn('useMapStyleSettings', 'Failed to fetch style settings', err)
      setError(errorMessage(err, 'Failed to fetch style settings'))
    } finally {
      setLoading(false)
    }
  }, [caseId, mapStyle])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const update = useCallback(
    async (input: UpdateMapStyleSettingsInput): Promise<MapStyleSettingsOutput | null> => {
      if (!caseId) return null
      try {
        const updated = await updateStyleSettings(caseId, mapStyle, input)
        setSettings(updated)
        setError(null)
        return updated
      } catch (err) {
        logger.warn('useMapStyleSettings', 'Failed to update style settings', err)
        setError(errorMessage(err, 'Failed to update style settings'))
        return null
      }
    },
    [caseId, mapStyle]
  )

  const setRootNode = useCallback(
    (nodeId: string | null): Promise<MapStyleSettingsOutput | null> =>
      update({ rootNodeId: nodeId }),
    [update]
  )

  return { settings, loading, error, updateSettings: update, setRootNode, refetch }
}
